<script setup lang="ts">
import EnglishLessonPage from "~/features/english/components/EnglishLessonPage.vue";

const textbookAlignedPoints = [
  "Lesson global warming ko Pakistan ke context me explain karta hai jahan geography, agriculture aur water dependence climate change ko zyada serious bana deti hai.",
  "Chapter me heatwaves, glacial melting, floods, droughts aur crop damage ko major impacts ke tor par highlight kiya gaya hai.",
  "Exam prep me causes, Pakistan ki vulnerability, sector-wise impacts aur practical measures sab ko clear headings me likhna useful hota hai.",
];

const chapterCoverage = [
  "Global warming ke causes",
  "Pakistan ki climate vulnerability",
  "Urban heatwaves and Karachi",
  "Glacial melting and water scarcity",
  "Floods, droughts, and extreme weather",
  "Agriculture par impact",
  "Adaptation, planning, and response",
];

const quickRevisionPoints = [
  "Pakistan glaciers, monsoon dependence aur agriculture ki wajah se climate-sensitive hai.",
  "Heatwaves urban areas me severe public-health risk create karti hain.",
  "Glacial melting short term me floods aur long term me water scarcity ka sabab ban sakti hai.",
  "Climate change rainfall patterns aur extreme weather events ko disturb karti hai.",
  "Agriculture crop loss, irrigation stress aur seasonal uncertainty ka shikar hoti hai.",
  "Best response planning, adaptation, water conservation aur disaster management ka combination hai.",
];

const readingQuestions = [
  {
    question: "What makes Pakistan particularly vulnerable to the impacts of global warming?",
    answer: "Pakistan ki diverse geography, glaciers, monsoon dependence aur agriculture-based economy usay climate change ke impacts ke liye especially vulnerable banati hai. Limited resources aur adaptation challenges bhi is vulnerability ko barha dete hain.",
  },
  {
    question: "How does rising temperature affect urban areas like Karachi?",
    answer: "Rising temperature se Karachi jese urban areas me severe heatwaves generate hoti hain jo public health, infrastructure aur daily life ko affect karti hain. Heat stress especially vulnerable populations ke liye dangerous hota hai.",
  },
  {
    question: "What is the connection between glacial melting and water scarcity in Pakistan?",
    answer: "Glacial melting short term me floods aur excess water flow create kar sakti hai, lekin long term me glacier shrink hone ki wajah se water availability reduce ho jati hai. Is se future water scarcity ka risk barhta hai.",
  },
  {
    question: "Why are extreme weather events becoming more frequent in Pakistan?",
    answer: "Climate change ne rainfall patterns aur temperature systems disturb kar diye hain. Isi disruption ki wajah se intense rain, floods, droughts aur unusual weather events zyada common ho rahe hain.",
  },
  {
    question: "How does global warming affect Pakistan's agriculture sector?",
    answer: "Global warming crop yield ko reduce karti hai, irrigation stress barhati hai aur farmers ke liye uncertainty paida karti hai. Heatwaves, floods aur changing seasons agriculture ko direct damage pohanchate hain.",
  },
  {
    question: "What practical steps can Pakistan take to face global warming?",
    answer: "Pakistan adaptation planning, heatwave management, water conservation, better disaster preparation aur sustainable development policies adopt kar sakta hai. Awareness aur technical capacity building bhi important hain.",
  },
];

const mcqs = [
  {
    question: "Which factor makes Pakistan highly vulnerable to climate change?",
    options: [
      { label: "a", text: "complete independence from agriculture", verdict: "incorrect", reason: "Pakistan agriculture-dependent hai, independent nahi." },
      { label: "b", text: "glacier systems and climate-sensitive geography", verdict: "correct", reason: "Lesson Pakistan ki geography, glaciers aur water dependence ko key vulnerability factors batata hai." },
      { label: "c", text: "absence of urban heat", verdict: "incorrect", reason: "Urban heat to climate stress ko aur serious banati hai." },
      { label: "d", text: "stable rainfall all year", verdict: "incorrect", reason: "Climate change ka issue hi disrupted weather patterns hain." },
    ],
  },
  {
    question: "What is a long-term effect of glacial melting in Pakistan?",
    options: [
      { label: "a", text: "permanent increase in safe water supply", verdict: "incorrect", reason: "Chapter long-term water shortage ka risk batata hai, permanent safety ka nahi." },
      { label: "b", text: "reduced concern about rivers", verdict: "incorrect", reason: "Glacier decline concern ko kam nahi, zyada serious banati hai." },
      { label: "c", text: "future water scarcity", verdict: "correct", reason: "Initial excess flow ke baad glacier shrinkage long-term water scarcity create karti hai." },
      { label: "d", text: "complete end of heatwaves", verdict: "incorrect", reason: "Heatwaves temperature rise se related hain, glacier melting unhen khatam nahi karti." },
    ],
  },
  {
    question: "Which response matches the lesson's practical approach to global warming?",
    options: [
      { label: "a", text: "ignoring urban heat because it is temporary", verdict: "incorrect", reason: "Urban heat ko serious climate stress ke tor par discuss kiya gaya hai." },
      { label: "b", text: "adaptation planning and water management", verdict: "correct", reason: "Lesson practical response me planning, preparedness aur water conservation ko support karta hai." },
      { label: "c", text: "depending only on weather luck", verdict: "incorrect", reason: "Chapter policy aur preparation ki need show karta hai, passive waiting ki nahi." },
      { label: "d", text: "focusing only on one city", verdict: "incorrect", reason: "Issue national level par agriculture, water aur health sab ko affect karta hai." },
    ],
  },
];

const answerBuilding = [
  "Pakistan ki vulnerability wale answer me geography aur agriculture dono mention karo.",
  "Effects ko urban heat, water scarcity aur agriculture damage ki headings me todna clear answer banata hai.",
  "Solutions me sirf tree plantation na likho; planning, adaptation aur management bhi include karo.",
  "Glacial melting ke answer me short-term flood risk aur long-term scarcity dono likho.",
  "Agar question practical steps par ho to awareness ke sath policy aur preparation ko bhi mention karo.",
];

usePageSeo({
  title: "Impact of Global Warming on Pakistan Notes | 11th Class English Unit 5",
  description: "11th class English Unit 5 notes with reading answers, climate effects on Pakistan, glacial melting, adaptation, MCQs and revision points.",
  keywords: "impact of global warming on pakistan notes, 11th class english unit 5, class 11 english chapter 5, global warming on pakistan short questions, climate change mcqs",
  breadcrumbs: [
    { name: "Home", item: "/" },
    { name: "11th English", item: "/11th/english/" },
    { name: "Prose", item: "/11th/english/prose/" },
    { name: "Impact of Global Warming on Pakistan", item: "/11th/english/prose/impact-of-global-warming-on-pakistan/" },
  ],
});
</script>

<template>
  <EnglishLessonPage
    unit="Unit 5"
    title="Impact of Global Warming on Pakistan"
    intro="Yeh page chapter ke Pakistan-focused climate impacts ko exam prep format me organize karta hai. Is unit me local examples ke sath causes, effects aur response samajhna important hota hai."
    :bullet-sections="[
      { title: 'Textbook-Aligned Snapshot', items: textbookAlignedPoints },
      { title: 'Unit 5 Coverage Checklist', items: chapterCoverage, layout: 'grid' },
      { title: 'Quick Revision Points', items: quickRevisionPoints },
      { title: 'Answer-Building Notes', items: answerBuilding }
    ]"
    :text-sections="[
      { title: 'Reading and Critical Thinking Answers', items: readingQuestions.map((item) => ({ title: item.question, description: item.answer })) }
    ]"
    :mcq-sections="[
      { title: 'MCQ with Option Explanation', items: mcqs }
    ]"
  />
</template>





